import Vue from 'vue'
import Axios from '@/config/axios'
import { isEmpty } from '@/helpers/object'
import {
  notifyExternalServices,
  ACTIONS,
} from '@/assets/javascripts/on/external-services/external-services'

// for release notes see: https://docs.adyen.com/checkout/components-drop-in-release-notes
export const ADYEN_SDK_URL =
  'https://checkoutshopper-live-us.adyen.com/checkoutshopper/sdk/3.0.0/adyen.js'
export const ADYEN_STYLES_URL =
  'https://checkoutshopper-live-us.adyen.com/checkoutshopper/sdk/3.0.0/adyen.css'

export const ADYEN_ENVS = {
  test: 'test',
  live: 'live',
}

// see: https://docs.adyen.com/checkout/payment-result-codes
export const ADYEN_PAYMENT_RESULT_CODES = {
  AuthenticationFinished: 'AuthenticationFinished', // not needed
  Authorised: 'Authorised',
  Cancelled: 'Cancelled',
  ChallengeShopper: 'ChallengeShopper', // not needed
  Error: 'Error',
  IdentifyShopper: 'IdentifyShopper', // not needed
  Pending: 'Pending',
  Received: 'Received',
  RedirectShopper: 'RedirectShopper', // handled by the backend
  Refused: 'Refused',
}

const PAYMENT_CONFIG_TYPES = {
  checkout: {
    paymentMethodUrlPath: 'api/checkout/payment/payment_methods',
  },
}

const PAYMENT_CONFIG_TYPE_NAMES = Object.keys(PAYMENT_CONFIG_TYPES).reduce((names, name) => {
  names[name] = name
  return names
}, {})

const ORIGIN_KEY_URL_PATH = 'api/checkout/payment/origin_key'
const MAKE_PAYMENT_URL_PATH = 'api/checkout/payment/payments'
const PAYMENT_DETAILS_URL_PATH = 'api/checkout/payment/payments/details'
const APPLE_PAY_AUTHORIZE_MERCHANT_URL_PATH = 'applepay_merchants/authorize'

const getRootUrl = (rootGetters, path) => rootGetters['page/getRootUrl'](path)
const getOrigin = () => window.location.origin

export default {
  namespaced: true,
  state: {
    originKeysByOrigin: {},
    paymentMethodsByType: {},
  },
  mutations: {
    setOrginKey(state, { origin, originKey }) {
      Vue.set(state.originKeysByOrigin, origin, originKey)
    },
    setPaymentMethods(state, { type, paymentMethods }) {
      Vue.set(state.paymentMethodsByType, type, paymentMethods)
    },
  },
  actions: {
    async fetchCheckoutPaymentConfiguration({ dispatch }) {
      dispatch('fetchPaymentConfiguration', { type: PAYMENT_CONFIG_TYPE_NAMES.checkout })
    },

    async fetchPaymentConfiguration({ dispatch }, { type }) {
      dispatch('fetchOriginKey')
      dispatch('fetchPaymentMethods', { type })
    },

    async fetchOriginKey({ commit, rootGetters }) {
      const url = getRootUrl(rootGetters, ORIGIN_KEY_URL_PATH)
      const origin = getOrigin()
      const response = await Axios.post(url, { origin_domain: origin })
      commit('setOrginKey', { origin, originKey: response.data.originKeys[origin] })
    },

    async fetchPaymentMethods({ commit, rootGetters }, { type }) {
      if (!PAYMENT_CONFIG_TYPES[type])
        throw new TypeError(`Invalid payment configiration type: ${type}.`)
      const url = getRootUrl(rootGetters, PAYMENT_CONFIG_TYPES[type].paymentMethodUrlPath)
      const response = await Axios.get(url)
      commit('setPaymentMethods', { type, paymentMethods: response.data })
    },

    makePayment({ rootGetters }, { data, orderNumber }) {
      const url = getRootUrl(rootGetters, MAKE_PAYMENT_URL_PATH)
      // TODO: B2C-808 define client-side redirect url here instead of hardcoding it in the backend
      const paymentDetailsURLPath =
        PAYMENT_DETAILS_URL_PATH + '/' + data?.paymentMethod?.type + '/' + orderNumber
      data.returnUrl = getRootUrl(rootGetters, paymentDetailsURLPath)
      data.browserInfo = {
        colorDepth: window.screen.colorDepth,
        screenHeight: window.screen.height,
        screenWidth: window.screen.width,
        timeZoneOffset: new Date().getTimezoneOffset(),
        javaEnabled: navigator.javaEnabled(),
      }
      if (data?.storePaymentMethod) {
        notifyExternalServices({
          action: ACTIONS.userAccount,
          details: { action: 'payment', label: 'save_payment' },
        })
      }
      return Axios.post(url, { data }).then(({ data }) => data)
    },

    authorizeApplePayMerchant({ rootGetters }, { validationURL }) {
      const url = getRootUrl(rootGetters, APPLE_PAY_AUTHORIZE_MERCHANT_URL_PATH)
      return Axios.post(url, { url: validationURL }).then(({ data }) => data)
    },
  },

  getters: {
    originKey: state => state.originKeysByOrigin[getOrigin()] || '',
    getPaymentMethods: state => type => state.paymentMethodsByType[type] || {},
    checkoutPaymentMethods: (state, getters) =>
      getters.getPaymentMethods(PAYMENT_CONFIG_TYPE_NAMES.checkout),
    getIsPaymentConfigurationLoaded: (state, getters) => type =>
      !!getters.originKey && !isEmpty(getters.getPaymentMethods(type)),
    isCheckoutPaymentConfigurationLoaded: (state, getters) =>
      getters.getIsPaymentConfigurationLoaded(PAYMENT_CONFIG_TYPE_NAMES.checkout),
  },
}
